/**
 * subject-summary.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import { AlgorithmService } from "../algorithms/management/algorithm.service";
import { UserService } from "../auth/identity/user.service";
import { DebugService } from "../debug/debug.service";

import { Subject } from "../subjects/subject";

/**
 * The {@link SubjectSummaryComponent} presents information about a particular
 * {@link Subject} and access to common actions, such as watching & unwatching,
 * running available algorithms on the subject, and browsing & interacting with
 * the subject's {@link Relationship}s.
 */
@Component({
  template: `
  <div class="container">
    <h3>
      Subject Summary
      <small class="capitalized">[ {{subject?.type}} ]</small>
    </h3>
    <div class="row">
      <div class="col-sm-7">
        <div class="well">
          <em>
            NOTE: If you've edited this subject this session, the summary may
            have fallen out of date.
          </em>
          <table class="table">
            <tr>
              <th class="text-right">Created at:</th>
              <td>{{subject?.created | date:"medium"}}</td>
            </tr>
            <tr *ngFor="let datum of subject?.summary">
              <th class="capitalized text-right">
                {{datum.displayName || datum.name}}:
              </th>
              <td>
                <typed-display [input]="datum"></typed-display>
              </td>
            </tr>
          </table>

          <h4>Available Algorithms</h4>
          <table class="table center-items">
            <tr *ngFor="let alg of subject?.availableAlgorithms; let i = index">
              <td>
                <button
                  class="btn boxier p100"
                  [ngClass]="colorCycle(i)"
                  (click)="algorithms.open(alg, subject)">
                  {{alg.displayName}}
                </button>
              </td>
              <td>
                <span *ngIf="!alg.supportsIntake" class="badge">read-only</span>
                <p *ngIf="alg.description" [innerHtml]="alg.description"></p>
                <em *ngIf="!alg.description">No description available.</em>
              </td>
            </tr>
          </table>
        </div>
      </div>


      <!-- SIDEBAR -->
      <div class="col-sm-5">
        <button
          *ngIf="users.debugMode && !debug.startupSubjectTarget"
          class="btn btn-default debug-only form-control"
          (click)="debug.setStartupTarget(subject)">
          «set as debug startup target»
        </button>
        <button
          *ngIf="users.debugMode && debug.startupSubjectTarget"
          class="btn btn-default debug-only form-control"
          (click)="debug.clearStartupTarget()">
          «remove current startup subject target»
        </button>

        <watch-button [subject]="subject"></watch-button>
        <relationship-panel [subject]="subject" [panelOpen]="true">
        </relationship-panel>
      </div>
    </div>
  `,
  styles: [
    ".boxier {padding-top: 1em; padding-bottom: 1em; height: 3.5em;}",
    ".btn.debug-only {margin-bottom: 20px;}"
  ]
})
export class SubjectSummaryComponent implements OnInit
{
  /**
   * The subject this page refers to (the component is guarded in a way that
   * ensures there is a subject referent; the object is loaded through a
   * resolver)
   */
  public subject : Subject = null;

  /**
   * "Cycle" through the standard color classes to provide a rainbow of buttons.
   */
  public colorCycle (i : number) : string
  {
    return [
      "btn-info",
      "btn-warning",
      "btn-danger",
      "btn-success",
      "btn-default"
      ][i % 5];
  }

  constructor (
    // protected subjects : SubjectService,
    protected algorithms : AlgorithmService,
    protected router : Router,
    protected route : ActivatedRoute,
    protected users : UserService,
    protected debug : DebugService
  ) {}

  public ngOnInit () : void
  {
    this.route.data.subscribe((data : any) => {
      if (data.subject instanceof Subject)
      {
        this.subject = data.subject;
      }
    });
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/intake-form/subject-summary.component.ts